import { useEffect, useMemo, useState } from "react";

import { Badge, Button, Card, Empty, Input, Popover, Select, Table, Tag, Typography, message } from "antd";

import { useNavigate } from "react-router";
import { ArrowLeftOutlined, CaretDownOutlined, CaretUpOutlined, DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import filterIcon from "../../../assets/filter.png";
import { LEADERSHIP_ATTENTION_DOWNLOAD_API_URL } from "../../../config/constants";
import { apiFetch } from "../../../utils/apiClient";
import { PillPagination } from "../../../components/PillPagination";
import { useLeadershipAttentionQuery } from "./leadershipAttention.hooks";
import "../../../styles/trackon.css"

const mapCommentToEntry = (comment) => ({
  description: comment.description,
  remark: comment.remarks ?? undefined,
});

const { Title, Text } = Typography;

const DEFAULT_PARAMS = { page: 0, size: 10 };
const SEARCH_DEBOUNCE_MS = 400;
const TIMELINE_COLORS = ["#2563EB", "#16A34A", "#7C3AED", "#F59E0B", "#0D9488"];

const getRowKey = (record) => record.id ?? record.projectId ?? record.projectName;

// Falls back to the single description/remark pair when the API doesn't send a list.
const getEntries = (record) => {
  if (Array.isArray(record.leadershipComments) && record.leadershipComments.length > 0) {
    return record.leadershipComments.filter((comment) => !comment.deleted).map(mapCommentToEntry);
  }

  if (Array.isArray(record.descriptions) && record.descriptions.length > 0) {
    return record.descriptions;
  }

  const description = record.description ?? record.comments;
  const remark = record.remark;

  return description || remark ? [{ description, remark }] : [];
};

const getAuthHeader = () => {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  if (!token) return {};

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";
  const normalizedToken = token
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  if (!normalizedToken) return {};
  return { Authorization: `${tokenType} ${normalizedToken}` };
};

export default function LeadershipAttentionView() {
  const navigate = useNavigate();
  const FILTER_POPUP_WIDTH = 320;
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [searchInput, setSearchInput] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftAccountName, setDraftAccountName] = useState(undefined);
  const [draftContractCategory, setDraftContractCategory] = useState(undefined);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const { data, isFetching, isError, error } = useLeadershipAttentionQuery(params);

  const toggleExpandedRow = (key) => {
    setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]));
  };

  useEffect(() => {
    if (isError && error instanceof Error) {
      message.error(error.message || "Failed to load leadership attention projects.");
    }
  }, [isError, error]);

  // Debounce search input before hitting the backend, resetting to page 0.
  useEffect(() => {
    const handle = setTimeout(() => {
      setParams((prev) => {
        const nextSearchText = searchInput.trim() || undefined;
        if (prev.searchText === nextSearchText) return prev;
        return { ...prev, searchText: nextSearchText, page: 0 };
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchInput]);

  const accountNameOptions = useMemo(
    () => (data?.accountNames ?? []).map((accountName) => ({ value: accountName, label: accountName })),
    [data?.accountNames],
  );

  const contractCategoryOptions = useMemo(
    () => (data?.contractCategories ?? []).map((category) => ({ value: category, label: category })),
    [data?.contractCategories],
  );

  const handleFilterOpenChange = (open) => {
    setFilterOpen(open);

    if (open) {
      setDraftAccountName(params.accountName);
      setDraftContractCategory(params.contractCategory);
    }
  };

  const applyFilters = () => {
    setParams((prev) => ({
      ...prev,
      accountName: draftAccountName || undefined,
      contractCategory: draftContractCategory || undefined,
      page: 0,
    }));
    setFilterOpen(false);
  };

  const getSortOrder = (field) => {
    if (params.sortBy !== field) return undefined;
    return params.sortDirection === "asc" ? "ascend" : "descend";
  };

  const handleTableChange = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortBy = singleSorter?.order ? String(singleSorter.columnKey ?? singleSorter.field) : undefined;
    const sortDirection = singleSorter?.order === "ascend" ? "asc" : singleSorter?.order === "descend" ? "desc" : undefined;

    setParams((prev) => ({ ...prev, sortBy, sortDirection }));
  };

  const handlePageChange = (page) => {
    setParams((prev) => ({ ...prev, page: page - 1 }));
  };

  const handlePageSizeChange = (size) => {
    setParams((prev) => ({ ...prev, size }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setDraftAccountName(undefined);
    setDraftContractCategory(undefined);
    setParams(DEFAULT_PARAMS);
    setFilterOpen(false);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await apiFetch(LEADERSHIP_ATTENTION_DOWNLOAD_API_URL, {
        method: "GET",
        headers: {
          Accept: "application/octet-stream",
          ...getAuthHeader(),
        },
      }, "Failed to download leadership attention report.");

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Leadership_Attention_${Date.now()}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error("Error downloading leadership attention report:", downloadError);
      message.error("Failed to download leadership attention report.");
    } finally {
      setIsDownloading(false);
    }
  };

  const activeFilterCount = [params.accountName, params.contractCategory].filter(Boolean).length;

  const filterContent = (
    <div style={{ width: "100%", padding: "4px 0 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          width: FILTER_POPUP_WIDTH,
          maxWidth: "calc(100vw - 24px)",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14 }}>Filters</span>
        {activeFilterCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={clearFilters}
            style={{ padding: 0, height: "auto", fontSize: 12 }}
          >
            Clear all
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#595959", fontWeight: 600, marginBottom: 6 }}>Account Name</div>
        <Select
          size="middle"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="None"
          value={draftAccountName}
          onChange={(value) => setDraftAccountName(value)}
          options={accountNameOptions}
          style={{ width: "100%" }}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "#595959", fontWeight: 600, marginBottom: 6 }}>Contract Category</div>
        <Select
          size="middle"
          allowClear
          placeholder="None"
          value={draftContractCategory}
          onChange={(value) => setDraftContractCategory(value)}
          options={contractCategoryOptions}
          style={{ width: "100%" }}
        />
      </div>

      <Button type="primary" block onClick={applyFilters} style={{ borderRadius: 8 }}>
        Apply Filters
      </Button>
    </div>
  );

  const columns = [
   
{
  title: "Project Name",
  dataIndex: "projectName",
  key: "projectName",
  width: 220,
  align: "left",
  sorter: true,
  sortOrder: getSortOrder("projectName"),
  render: (value, record) => {
    const entries = getEntries(record);
    const key = getRowKey(record);

    const projectContent = (
      <div>
        <div
          style={{
            fontWeight: 600,
            color: "#3c3c3e",
            lineHeight: "20px",
          }}
        >
          {value ?? "-"}
        </div>

<div
  style={{
    fontSize: 12,
    color: "#2563EB",
    marginTop: 2,
    lineHeight: "18px",
    cursor: record.projectId ? "pointer" : "default",
    textDecoration: record.projectId ? "underline" : "none",
  }}
  onClick={() => {
    if (record.projectId) {
      navigate(`/watchtower/projects/${record.projectId}`);
    }
  }}
>
  {record.projectId ?? "-"}
</div>
      </div>
    );

    if (entries.length === 0) {
      return projectContent;
    }

    return (
      <a
        onClick={() => toggleExpandedRow(key)}
        style={{
          display: "block",
          cursor: "pointer",
          textDecoration: "none",
        }}
      >
        {projectContent}
      </a>
    );
  },
},
    {
      title: "Contract Category",
      dataIndex: "contractCategory",
      key: "contractCategory",
      width: 180,
      align: "left",
      sorter: true,
      sortOrder: getSortOrder("contractCategory"),
      render: (value) => <Text style={{ color: "#344054" }}>{value ?? "-"}</Text>,
    },
    {
      title: "Customer Name",
      dataIndex: "accountName",
      key: "accountName",
      width: 160,
      align: "left",
      sorter: true,
      sortOrder: getSortOrder("accountName"),
      render: (value) => <Text style={{ color: "#344054" }}>{value ?? "-"}</Text>,
    },
    
  ];

  const renderDescriptionsAndRemarks = (record) => {
    const entries = getEntries(record);

    return (
      <div style={{ background: "#F8FAFC", borderRadius: 10, padding: 16 }}>
        <Text style={{ display: "block", fontWeight: 600, color: "#3c3c3e", marginBottom: 12 }}>
          Descriptions &amp; Remarks for {record.projectName}
        </Text>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "48px 1fr 1fr",
            gap: 12,
            padding: "0 8px 8px",
            borderBottom: "1px solid #E2E8F0",
            fontSize: 12,
            fontWeight: 600,
            color: "#64748B",
            textTransform: "uppercase",
          }}
        >
          <div>#</div>
          <div>Description</div>
          <div>Remark</div>
        </div>

        {entries.map((entry, index) => {
          const color = TIMELINE_COLORS[index % TIMELINE_COLORS.length];

          return (
            <div
              key={`${entry.description ?? ""}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr 1fr",
                gap: 12,
                alignItems: "center",
                padding: "12px 8px",
                borderBottom: index === entries.length - 1 ? "none" : "1px solid #EEF2F6",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: color,
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {index + 1}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <Text style={{ color: "#3c3c3e" }}>{entry.description || "-"}</Text>
                <div style={{ flex: 1, borderBottom: "1px dotted #CBD5E1", minWidth: 24 }} />
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
              </div>

              <div
                style={{
                  background: entry.remark ? `${color}1A` : undefined,
                  color: entry.remark ? color : "#94A3B8",
                  borderRadius: 8,
                  padding: "4px 10px",
                  fontWeight: entry.remark ? 500 : 400,
                  fontSize: 13,
                  width: "fit-content",
                  wordBreak: "break-word",
                }}
              >
                {entry.remark || "-"}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const projects = data?.projects ?? [];
  const totalProjects = data?.totalProjects ?? 0;
  const currentPageSize = data?.pageSize ?? params.size ?? 10;
  const currentPage = (data?.currentPage ?? params.page ?? 0) + 1;

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          background: "#ffffff",
          border: "1px solid #E8EDF5",
          boxShadow: "0 2px 14px rgba(15, 23, 42, 0.04)",
        }}
        bodyStyle={{ padding: 24 }}
      >

        <Card
          style={{
            borderRadius: 12,
            marginBottom: 16,
            border: "1px solid #f0f0f0",
          }}
          styles={{ body: { padding: "14px 20px" } }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <Input
                size="large"
                prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                placeholder="Search by project or Customer Name ..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                allowClear
                style={{ borderRadius: 8, fontSize: 13, width: "100%" }}
              />
            </div>

            <Popover
             overlayClassName="trackon-root"
              content={filterContent}
              trigger="click"
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              placement="leftTop"
              arrow={false}
              overlayStyle={{
                borderRadius: 12,
                maxWidth: "calc(100vw - 24px)",
                maxHeight: "calc(100vh - 24px)",
              }}
              styles={{
                root: {
                  maxWidth: "calc(100vw - 24px)",
                },
                body: {
                  borderRadius: 12,
                  padding: 12,
                  width: FILTER_POPUP_WIDTH,
                  maxWidth: "calc(100vw - 24px)",
                  overflow: "visible",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
                <Button
                  size="large"
                  icon={<img src={filterIcon} alt="Filter" style={{ width: 16, height: 16 }} />}
                  style={{
                    borderRadius: 8,
                    borderColor: activeFilterCount > 0 ? "#1677ff" : undefined,
                    color: activeFilterCount > 0 ? "#1677ff" : undefined,
                    fontWeight: activeFilterCount > 0 ? 600 : undefined,
                    whiteSpace: "nowrap",
                  }}
                >
                  Filter
                </Button>
              </Badge>
            </Popover>

            <Button
              size="large"
              icon={<DownloadOutlined />}
              onClick={() => {
                void handleDownload();
              }}
              loading={isDownloading}
            >
              Download
            </Button>
          </div>
        </Card>

        <Table
          columns={columns}
          dataSource={projects}
          rowKey={getRowKey}
          loading={isFetching}
          onChange={handleTableChange}
          bordered
          size="middle"
          rowClassName={() => "leadership-attention-row"}
          style={{ width: "100%" }}
          scroll={{ x: "max-content" }}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
            expandedRowRender: renderDescriptionsAndRemarks,
            rowExpandable: (record) => getEntries(record).length > 0,
            expandIcon: ({ expanded, onExpand, record }) =>
              getEntries(record).length > 0 ? (
                <Button
                  type="text"
                  size="small"
                  icon={<CaretDownOutlined style={{ transform: expanded ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }} />}
                  onClick={(event) => onExpand(record, event)}
                  style={{ padding: 0, width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                />
              ) : null,
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No leadership attention records found."
              />
            ),
          }}
        />

        <PillPagination
          currentPage={currentPage}
          pageSize={currentPageSize}
          total={totalProjects}
          itemLabel="projects"
          onPageChange={handlePageChange}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageSizeChange={handlePageSizeChange}
        />
      </Card>
    </div>
  );
}
